import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Kontakt - Havneloppen";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Kontakt Havneloppen i Nyborg. Se vores åbningstider, adresse på Knudshovedvej 38, ring til os eller send en besked via vores kontaktformular."
      );
    }

    fetch("https://api.dev.auxo.dk/public/content/sider")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const page = data.find((p) => p.url_slug === "kontakt");
          setPageData(page);
        }
      })
      .catch((err) => console.error("Error fetching contact page data:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-brand-blue text-warm-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/735543790-2155399195316387-8282195688578813024-n.jpg"
            alt="Kontakt Havneloppen"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange mb-4">
            <Link to="/" className="hover:text-warm-white transition-colors">FORSIDE</Link>
            <span className="text-stone-400">/</span>
            <span className="text-warm-white">KONTAKT</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-warm-white sm:text-4xl md:text-5xl">
            {pageData?.titel || "Kontakt Havneloppen"}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16 w-full">
        {/* Intro Subtext */}
        <div className="mb-12">
          <div
            className="prose text-dark-graphite-light text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: pageData?.indhold || "" }}
          />
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
        {/* Left: Contact Info & Opening Hours */}
        <div className="lg:col-span-5 space-y-8">
          {/* Quick Info Box */}
          <div className="rounded-2xl border border-warm-beige bg-white p-6 shadow-sm md:p-8 space-y-6">
            <h3 className="font-display text-xl font-bold text-brand-blue border-b border-warm-beige pb-4">
              Butiksoplysninger
            </h3>

            <ul className="space-y-5 text-sm">
              <li className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                  <i className="fa-solid fa-location-dot text-base" />
                </div>
                <div>
                  <strong className="block text-dark-graphite font-semibold">Adresse</strong>
                  <span className="text-dark-graphite-light">Knudshovedvej 38, 5800 Nyborg</span>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                  <i className="fa-solid fa-phone text-base" />
                </div>
                <div>
                  <strong className="block text-dark-graphite font-semibold">Telefonnummer</strong>
                  <a href="tel:+4528767915" className="text-dark-graphite-light hover:text-brand-orange transition-colors whitespace-nowrap">
                    28 76 79 15
                  </a>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                  <i className="fa-solid fa-envelope text-base" />
                </div>
                <div>
                  <strong className="block text-dark-graphite font-semibold">E-mailadresse</strong>
                  <a href="mailto:havneloppen@gmail.com" className="text-dark-graphite-light hover:text-brand-orange transition-colors whitespace-nowrap">
                    havneloppen@gmail.com
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Opening Hours Box */}
          <div className="rounded-2xl border border-warm-beige bg-white p-6 shadow-sm md:p-8 space-y-4">
            <h3 className="font-display text-xl font-bold text-brand-blue border-b border-warm-beige pb-4">
              Åbningstider i butikken
            </h3>
            <ul className="space-y-3 text-sm text-dark-graphite-light">
              <li className="flex justify-between border-b border-warm-beige/50 pb-2">
                <span className="font-medium text-dark-graphite">Mandag - Torsdag:</span>
                <span className="font-mono font-semibold text-brand-blue">10:00 - 18:00</span>
              </li>
              <li className="flex justify-between border-b border-warm-beige/50 pb-2 text-dark-graphite/30">
                <span className="font-medium">Fredag:</span>
                <span className="font-semibold">Lukket</span>
              </li>
              <li className="flex justify-between border-b border-warm-beige/50 pb-2">
                <span className="font-medium text-dark-graphite">Lørdag - Søndag:</span>
                <span className="font-mono font-semibold text-brand-blue">10:00 - 16:00</span>
              </li>
            </ul>
          </div>

          {/* Facebook Box */}
          <div className="rounded-2xl border border-brand-blue/10 bg-brand-blue/5 p-6 shadow-sm md:p-8 text-center space-y-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue">
              <i className="fa-brands fa-facebook text-2xl" />
            </div>
            <h4 className="font-display text-lg font-bold text-brand-blue">
              Følg os på Facebook
            </h4>
            <p className="text-xs text-dark-graphite-light leading-relaxed">
              Vi deler dagligt billeder af de nyeste loppefund, der lander i butikken. Følg med og reserver dine favoritter direkte!
            </p>
            <a
              href="https://www.facebook.com/p/Havneloppen-100063561880040/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-xs font-bold text-warm-white hover:bg-brand-orange-hover transition-colors"
            >
              Besøg Facebook-side
            </a>
          </div>
        </div>

        {/* Right: Contact Form */}
        <div className="lg:col-span-7">
          <ContactForm />
        </div>
      </div>
    </div>
  </div>
);
}
